import { useState, useEffect } from "react";
import type { FetchState } from "../types/api";

const baseUrl: string = process.env.REACT_APP_API_BASE_URL ?? "";

/**
 * Generic fetch hook with loading / error / abort.
 *
 * @param path  API path appended to REACT_APP_API_BASE_URL, or null to skip.
 * @param deps  Extra dependency array entries (default []).
 */
export default function useFetch<T = unknown>(
  path: string | null,
  deps: unknown[] = [],
): FetchState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(!!path);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }

    const ac = new AbortController();
    setLoading(true);
    setError("");

    (async () => {
      try {
        const r = await fetch(`${baseUrl}${path}`, { signal: ac.signal });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setData(await r.json());
      } catch (e: unknown) {
        if (e instanceof Error && e.name !== "AbortError") {
          console.error(e);
          setError(e.message || "Failed to load");
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);

  return { data, loading, error };
}

/** Re-export baseUrl for components that still do manual fetches. */
export { baseUrl };
